import {
  Button,
  CollectionPreferencesProps,
  Input,
  Link,
  Select,
  SelectProps,
  StatusIndicator,
  TableProps,
  Toggle
} from '@amzn/awsui-components-core';
import { ApplicationFlatTableEntity } from 'src/components/context/AppContextModels';
import { DEFAULT_LARGE_PAGE_SIZE } from 'src/components/generic-components/TableCommons';
import AppNameWithLink from 'src/components/generic-components/AppNameWithLink';
import { SHORT_DESCRIPTION_ERROR_MESSAGE, shortDescriptionRegexPattern } from './admin-manage-application/ApplicationFormValidations';
import React from 'react';
import { getReadableDateTimeFormat } from 'src/utilities/DateTimeUtilities';
import { isEligibleToEnableEmbedding } from '../../embedded-apps/utils/EmbeddingUtils';

export const DEFAULT_VISIBLE_CONTENT = [
  'status',
  'applicationName',
  'applicationLink',
  'shortApplicationDescription',
  'applicationOwner',
  'glAccount',
  'toolUI',
  'embeddingEnabled'
];

export const DEFAULT_VISIBLE_CONTENT_OPTIONS: CollectionPreferencesProps.VisibleContentOptionsGroup[] = [
  {
    label: 'Properties',
    options: [
      { id: 'status', label: 'Status', editable: false },
      { id: 'applicationName', label: 'Application', editable: false },
      { id: 'applicationLink', label: 'Link', editable: false },
      { id: 'shortApplicationDescription', label: 'Description', editable: false },
      { id: 'applicationOwner', label: 'Team', editable: true },
      { id: 'glAccount', label: 'Module', editable: true },
      { id: 'toolUI', label: 'Product UI', editable: true },
      { id: 'embeddingEnabled', label: 'Embedding', editable: true },
      { id: 'updatedBy', label: 'Updated By', editable: true },
      { id: 'updatedTime', label: 'Updated At', editable: true }
    ]
  }
];

export const APPLICATIONS_DEFAULT_PREFERENCES: CollectionPreferencesProps.Preferences = {
  pageSize: DEFAULT_LARGE_PAGE_SIZE,
  visibleContent: DEFAULT_VISIBLE_CONTENT,
  wrapLines: false,
  stripedRows: false,
  stickyColumns: { first: 2, last: 0 }
};

export const serverSideErrorsStore = new Map();

// Function to create column definitions with access to applications list
export const createApplicationColumnDefinitions = (allApplications: any[]): TableProps.ColumnDefinition<ApplicationFlatTableEntity>[] => [
  {
    id: 'status',
    header: 'Status',
    width: 176, // Cloudscape recommends to set a minimum width of at least 176px for columns that are editable.
    cell: (item: ApplicationFlatTableEntity) => (
      <StatusIndicator colorOverride={item.status === 'Active' ? 'green' : 'red'} type={item.status === 'Active' ? 'success' : 'error'}>
        {item.status}
      </StatusIndicator>
    ),
    editConfig: {
      ariaLabel: 'Status',
      editIconAriaLabel: 'editable',
      errorIconAriaLabel: 'Error',
      editingCell: (item, { currentValue, setValue }) => {
        const options = [
          { value: 'Active', label: 'Active' },
          { value: 'Inactive', label: 'Inactive' }
        ] as SelectProps.Option[];
        return (
          <Select
            autoFocus={true}
            expandToViewport={true}
            ariaLabel="Select desired state"
            options={options}
            onChange={(event) => {
              setValue(event.detail.selectedOption.value);
            }}
            selectedOption={options.find((option) => option.value === (currentValue ?? item.status)) || {}}
          />
        );
      }
    },
    sortingField: 'status'
  },
  {
    id: 'applicationName',
    header: 'Application',
    width: 200, // width verified
    cell: (item: ApplicationFlatTableEntity) => (
      <AppNameWithLink
        appName={item.applicationName}
        appId={item.id}
        embeddingEnabled={item.embeddingEnabled}
        applicationLink={item.applicationLink}
        urlName={item.urlName}
      />
    ),
    sortingField: 'applicationName'
  },
  {
    id: 'applicationLink',
    header: 'Link',
    width: 70,
    cell: (item: ApplicationFlatTableEntity) => (
      <>
        {item.applicationLink && (
          <Button iconName="external" variant="inline-icon" onClick={() => window.open(item.applicationLink, '_blank')}></Button>
        )}
        {!item.applicationLink && <></>}
      </>
    )
  },
  {
    id: 'shortApplicationDescription',
    header: 'Short Description',
    width: 480,
    cell: (item: ApplicationFlatTableEntity) => item.shortApplicationDescription,
    editConfig: {
      ariaLabel: 'Short Description',
      editIconAriaLabel: 'editable',
      errorIconAriaLabel: 'Error',
      validation(item, value) {
        if (serverSideErrorsStore.has(item)) {
          if (value) {
            serverSideErrorsStore.set(item, shortDescriptionRegexPattern.test(value) ? undefined : SHORT_DESCRIPTION_ERROR_MESSAGE);
          }
          return serverSideErrorsStore.get(item);
        }
      },
      editingCell: (item, { currentValue, setValue }) => {
        return (
          <Input
            autoFocus={true}
            ariaLabel="Edit short description"
            value={currentValue ?? item.shortApplicationDescription}
            onChange={(event) => {
              setValue(event.detail.value);
            }}
            placeholder="Enter short description"
          />
        );
      }
    },
    sortingField: 'shortApplicationDescription'
  },
  {
    id: 'embeddingEnabled',
    header: 'Embedding',
    width: 120,
    cell: (item: ApplicationFlatTableEntity) => {
      // Find the actual Application object from the list
      const application = allApplications.find((app) => app.id === item.id);
      if (!application) {
        return <StatusIndicator type="info">Not Found</StatusIndicator>;
      }

      // Check if application is eligible for embedding
      const isEligible = isEligibleToEnableEmbedding(application);

      if (!isEligible) {
        return <StatusIndicator type="info">Not Eligible</StatusIndicator>;
      }

      return <StatusIndicator type={item.embeddingEnabled ? 'success' : 'stopped'}>{item.embeddingEnabled ? 'Enabled' : 'Disabled'}</StatusIndicator>;
    },
    editConfig: {
      ariaLabel: 'Embedding Status',
      editIconAriaLabel: 'editable',
      errorIconAriaLabel: 'Error',
      disabledReason: (item: ApplicationFlatTableEntity) => {
        // Find the actual Application object from the list
        const application = allApplications.find((app) => app.id === item.id);

        // Check if application is eligible for embedding
        const isEligible = isEligibleToEnableEmbedding(application);

        if (!isEligible) {
          return `Not Eligible for Embedding`;
        }
        return undefined;
      },
      editingCell: (item, { currentValue, setValue }) => {
        return (
          <Toggle
            checked={currentValue ?? item.embeddingEnabled}
            onChange={(event) => {
              setValue(event.detail.checked);
            }}
            ariaLabel="Toggle embedding status"
          />
        );
      }
    },
    sortingField: 'embeddingEnabled'
  },
  {
    id: 'applicationOwner',
    header: 'Team',
    width: 120,
    cell: (item: ApplicationFlatTableEntity) => item.applicationOwner,
    sortingField: 'applicationOwner'
  },
  {
    id: 'glAccount',
    header: 'Module',
    width: 200,
    cell: (item: ApplicationFlatTableEntity) => item.glAccount,
    sortingField: 'glAccount'
  },
  {
    id: 'toolUI',
    header: 'Product UI',
    width: 50,
    cell: (item: ApplicationFlatTableEntity) => item.toolUI,
    sortingField: 'toolUI'
  },
  {
    id: 'updatedBy',
    header: 'Updated By',
    width: 150,
    cell: (item: ApplicationFlatTableEntity) => item.updatedBy,
    sortingField: 'updatedBy'
  },
  {
    id: 'updatedTime',
    header: 'Updated At',
    width: 150,
    cell: (item: ApplicationFlatTableEntity) => getReadableDateTimeFormat(item.updatedTime),
    sortingField: 'updatedTime'
  }
];
